import { Card, CardBody } from "@heroui/react";
import { useState } from "react";
import { useShallow } from "zustand/shallow";
import clsx from "clsx";

import { useBoundStore } from "@/app/store";
import { TodosList } from "@/features/todos/components/TodosList";

export function TodayTodos() {
  const [exitingTodoIds, setExitingTodoIds] = useState<number[]>([]);
  const userId = useBoundStore(useShallow((state) => state.user.id));
  const { todos, updateTodo, fetchDailyStats } = useBoundStore(
    useShallow((state) => ({
      todos: state.todos.items,
      updateTodo: state.todos.updateTodo,
      fetchDailyStats: state.dailyStats.fetchDailyStats,
    }))
  );

  // Get today's todos ordered by priority (higher priority first)
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const todaysTodos = todos
    .filter((todo) => {
      // Keep todos that are currently exiting to show animation
      if (exitingTodoIds.includes(todo.id)) return true;
      if (todo.done) return false;
      if (!todo.due_date) return false;
      const dueDate = new Date(todo.due_date);
      return dueDate >= today && dueDate < tomorrow;
    })
    .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0))
    .slice(0, 3);

  const totalTodaysCount = todos.filter((todo) => {
    if (todo.done) return false;
    if (!todo.due_date) return false;
    const dueDate = new Date(todo.due_date);
    return dueDate >= today && dueDate < tomorrow;
  }).length;

  const handleToggleTodo = async (id: number) => {
    const todo = todos.find((t) => t.id === id);
    if (todo) {
      // Mark as exiting to keep visible during animation
      if (!todo.done) {
        setExitingTodoIds((prev) => [...prev, id]);
        // Remove from exiting state after animation completes
        setTimeout(() => {
          setExitingTodoIds((prev) => prev.filter((tid) => tid !== id));
        }, 650);
      }

      await updateTodo(id, {
        done: !todo.done,
        done_at: !todo.done ? new Date().toISOString() : null,
      });

      if (userId) {
        await fetchDailyStats(userId, "today");
      }
    }
  };

  return (
    <Card
      className={clsx(
        "col-span-2 h-40 transition-all duration-300 sm:col-span-2",
        "dark:from-content1 bg-linear-to-br from-white to-neutral-100 dark:via-neutral-900 dark:to-neutral-950",
        "ring-1 ring-neutral-200 dark:ring-neutral-700/50"
      )}
    >
      <CardBody className="overflow-hidden">
        <div className="flex justify-between">
          <span className="block text-lg font-semibold">My work</span>
          {totalTodaysCount > 3 && <span className="text-default-500 text-sm">+{totalTodaysCount - 3} more</span>}
        </div>

        {todaysTodos.length > 0 ? (
          <div className="mt-1">
            <TodosList
              items={todaysTodos.map((todo) => ({
                id: todo.id,
                text: todo.text,
                done: todo.done,
                doneAt: todo.done_at,
                dueDate: todo.due_date ? new Date(todo.due_date) : null,
                priority: todo.priority,
                children: [],
                collapsed: false,
              }))}
              onToggleDone={handleToggleTodo}
            />
          </div>
        ) : (
          <div className="text-default-500 mt-4 py-4 text-center">No tasks for today!</div>
        )}
      </CardBody>
    </Card>
  );
}
